import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { TIMEOUT_EXTRACAO_IA } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Package, Plus, Lock, Search, Weight, DollarSign,
  TrendingUp, FileText, Calendar, ChevronLeft, ChevronRight, X,
  GitBranch, CheckCircle2, Trash2, Printer, ReceiptText, Edit2, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';
import { validarQuantidadePositiva } from '../lib/validators';

// ============================================================
// CONSTANTES
// ============================================================
const STATUS_OPTIONS = ['PENDENTE', 'IMPLANTADO', 'NF_EMITIDA', 'CANCELADO'];

const STATUS_CONFIG = {
  PENDENTE:   { label: 'Pendente',   cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  IMPLANTADO: { label: 'Implantado', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  NF_EMITIDA: { label: 'NF Emitida', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELADO:  { label: 'Cancelado',  cls: 'bg-slate-100 text-slate-500 border-slate-300' },
  ATRASADO:   { label: 'Atrasado',   cls: 'bg-red-100 text-red-700 border-red-300' },
};


const ENTREGA_FORM_INITIAL = {
  qtde_entregue: '', motivo: 'cliente_aceitou', observacao: '',
};


const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

// ============================================================
// HELPERS
// ============================================================
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);
const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const toNum = (v) => { if (!v && v !== 0) return 0; if (typeof v === 'number') return v; return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0; };
const fmtDate = (d) => { if (!d) return '---'; return new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }); };
const nomeFantasia = (nomeCompleto) => {
  if (!nomeCompleto) return '---';
  const stop = ['S/A','SA','LTDA','LTDA-ME','ME','EIRELI','IND','COM','E','DE','DO','DA','DOS','DAS','IND.','COM.','S.A.'];
  const partes = nomeCompleto.toUpperCase().split(' ');
  const significativas = partes.filter(p => !stop.includes(p) && p.length > 1);
  return significativas.slice(0, 2).join(' ') || nomeCompleto.split(' ')[0];
};

const getDataFiltro = (p, mode) => {
  const raw = mode === 'cadastro' ? p.criado_em : p.data_entrega;
  return raw ? raw.substring(0, 10) : '';
};

const classificarSegmento = (fe) => {
  if (!fe) return 'OUTROS';
  const u = fe.toUpperCase();
  if (u.endsWith('-CX') || u.endsWith('-SAWER') || u.endsWith('-CV')) return 'CAIXA';
  if (u.endsWith('-CH') || u.endsWith('-OMC')) return 'CHAPA';
  return 'OUTROS';
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const inpSm = "w-full border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-800 outline-none focus:border-[#0A3D73] transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const getVariacaoInfo = (qtdePedido, qtdeEntregue) => {
  if (!qtdePedido || !qtdeEntregue) return null;
  const variacao = ((qtdeEntregue - qtdePedido) / qtdePedido) * 100;
  const abs = Math.abs(variacao);
  const sinal = variacao >= 0 ? '+' : '';
  if (abs <= 10) return { cor: 'bg-emerald-100 text-emerald-800 border-emerald-300', semaforo: '🟢', label: `${sinal}${variacao.toFixed(1)}%`, ok: true };
  if (abs <= 20) return { cor: 'bg-orange-100 text-orange-800 border-orange-300', semaforo: '🟡', label: `${sinal}${variacao.toFixed(1)}%`, ok: false };
  return { cor: 'bg-red-100 text-red-800 border-red-300', semaforo: '🔴', label: `${sinal}${variacao.toFixed(1)}%`, ok: false };
};

const LOGO_B64 = "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";


// ============================================================
// DATE RANGE PICKER
// ============================================================
const DateRangePicker = ({ dateStart, dateEnd, onChange, onClear, mode, onModeChange }) => {
  const today = new Date();
  const [open, setOpen] = React.useState(false);
  const [viewDate, setViewDate] = React.useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selecting, setSelecting] = React.useState('start');
  const [hoverDate, setHoverDate] = React.useState(null);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const parseD = (s) => s ? new Date(s + 'T00:00:00') : null;

  const handleDayClick = (day) => {
    const clicked = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const str = toStr(clicked);
    if (selecting === 'start') { onChange({ start: str, end: null }); setSelecting('end'); }
    else {
      const start = parseD(dateStart);
      if (clicked < start) onChange({ start: str, end: dateStart });
      else onChange({ start: dateStart, end: str });
      setSelecting('start'); setOpen(false);
    }
  };

  const inRange = (day) => {
    const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const s = parseD(dateStart); const e = parseD(dateEnd) || parseD(hoverDate);
    if (!s) return false;
    if (!e) return toStr(d) === dateStart;
    return d >= s && d <= e;
  };
  const isStart = (day) => toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === dateStart;
  const isEnd = (day) => toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === (dateEnd || hoverDate);

  const daysInMonth = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
  const firstDay = getFirstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth());

  const modoLabel = mode === 'cadastro' ? 'Cadastro' : 'Entrega';

  const labelBtn = () => {
    if (dateStart && dateEnd) return `${modoLabel}: ${fmtDate(dateStart)} → ${fmtDate(dateEnd)}`;
    if (dateStart) return `${modoLabel}: a partir de ${fmtDate(dateStart)}`;
    return 'Filtrar por Data';
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1">
        <button onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-2 h-9 px-3 text-xs font-bold rounded-lg border transition-all ${dateStart ? 'bg-[#0A3D73] text-white border-[#0A3D73]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0A3D73]'}`}>
          <Calendar size={13}/><span className="hidden sm:inline">{labelBtn()}</span>
        </button>
        {(dateStart || dateEnd) && (
          <button onClick={onClear} className="h-9 w-9 flex items-center justify-center bg-white border border-slate-200 rounded-lg hover:border-red-400 hover:text-red-500 transition-all text-slate-400">
            <X size={13}/>
          </button>
        )}
      </div>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-slate-200 shadow-2xl rounded-xl w-72 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A3D73] text-white">
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()-1, 1))} className="p-1 hover:bg-white/10 rounded-lg"><ChevronLeft size={14}/></button>
            <span className="text-xs font-black uppercase tracking-widest">{MESES[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()+1, 1))} className="p-1 hover:bg-white/10 rounded-lg"><ChevronRight size={14}/></button>
          </div>
          <div className="flex px-3 pt-2 gap-1.5">
            {[{k:'entrega',l:'Data de Entrega'},{k:'cadastro',l:'Data de Cadastro'}].map(o => (
              <button key={o.k} onClick={() => onModeChange(o.k)}
                className={`flex-1 text-xs font-bold py-1.5 rounded-lg border transition-all ${mode === o.k ? 'bg-[#0A3D73] text-white border-[#0A3D73]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#0A3D73]'}`}>
                {o.l}
              </button>
            ))}
          </div>
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 mt-2">
            <p className="text-xs font-bold text-slate-400">{selecting === 'start' ? '① Selecione a data inicial' : '② Selecione a data final'}</p>
          </div>
          <div className="grid grid-cols-7 px-3 pt-2">
            {DIAS_SEMANA.map(d => <div key={d} className="text-center text-xs font-black text-slate-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 px-3 pb-3 gap-y-0.5">
            {Array.from({length: firstDay}).map((_,i) => <div key={`e-${i}`}/>)}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const day = i+1;
              const iR = inRange(day); const iS = isStart(day); const iE = isEnd(day);
              const isT = toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === toStr(today);
              return (
                <button key={day} onClick={() => handleDayClick(day)}
                  onMouseEnter={() => { if (selecting === 'end' && dateStart) setHoverDate(toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day))); }}
                  onMouseLeave={() => setHoverDate(null)}
                  className={`h-8 w-full text-xs font-bold rounded transition-all
                    ${iS||iE ? 'bg-[#0A3D73] text-white' : ''}
                    ${iR&&!iS&&!iE ? 'bg-blue-100 text-blue-800' : ''}
                    ${!iR&&!iS&&!iE ? 'hover:bg-slate-100 text-slate-700' : ''}
                    ${isT&&!iS&&!iE ? 'ring-1 ring-[#0A3D73] text-[#0A3D73] font-black' : ''}`}>
                  {day}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap gap-1.5">
            {[
              {label:'Este mês', fn:()=>{const n=new Date();onChange({start:toStr(new Date(n.getFullYear(),n.getMonth(),1)),end:toStr(new Date(n.getFullYear(),n.getMonth()+1,0))});setSelecting('start');setOpen(false);}},
              {label:'Mês anterior', fn:()=>{const n=new Date();onChange({start:toStr(new Date(n.getFullYear(),n.getMonth()-1,1)),end:toStr(new Date(n.getFullYear(),n.getMonth(),0))});setSelecting('start');setOpen(false);}},
              {label:'Este ano', fn:()=>{const n=new Date();onChange({start:`${n.getFullYear()}-01-01`,end:`${n.getFullYear()}-12-31`});setSelecting('start');setOpen(false);}},
            ].map(s => (
              <button key={s.label} onClick={s.fn} className="text-xs font-bold px-2 py-1 bg-slate-100 hover:bg-[#0A3D73] hover:text-white rounded transition-all text-slate-600">{s.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const Pedidos = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [clientesBase, setClientesBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('TODOS');
  const [filterSegmento, setFilterSegmento] = useState('TODOS');
  const [filterCliente, setFilterCliente] = useState('');
  const [filterRepresentada, setFilterRepresentada] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [dateFilterMode, setDateFilterMode] = useState('entrega');
  const [entregaDialog, setEntregaDialog] = useState(false);
  const [entregaPedido, setEntregaPedido] = useState(null);
  const [entregaForm, setEntregaForm] = useState(ENTREGA_FORM_INITIAL);
  const [submittingEntrega, setSubmittingEntrega] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletePedido, setDeletePedido] = useState(null);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfForm, setPdfForm] = useState({ data_inicio: '', data_fim: '', status: 'TODOS', ids: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pedidoPrintAtual, setPedidoPrintAtual] = useState(null);
  const [importarDialog, setImportarDialog] = useState(false);
  const [importarStep, setImportarStep] = useState('upload');
  const [importarImage, setImportarImage] = useState(null);
  const [importarExtract, setImportarExtract] = useState([]);
  const [loadingExtract, setLoadingExtract] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [importarResult, setImportarResult] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [r1, r2, r3] = await Promise.all([api.get('/pedidos'), api.get('/materiais'), api.get('/clientes')]);
        setPedidos(Array.isArray(r1.data) ? r1.data : []);
        setMateriais(Array.isArray(r2.data) ? r2.data : []);
        setClientesBase(Array.isArray(r3.data) ? r3.data : []);
      } catch { toast.error('Erro ao sincronizar dados'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const fetchPedidos = async () => {
    try { const r = await api.get('/pedidos'); setPedidos(Array.isArray(r.data) ? r.data : []); }
    catch { toast.error('Erro ao carregar pedidos'); }
  };

  const handleAbrirImportar = () => {
    setImportarStep('upload');
    setImportarImage(null);
    setImportarExtract([]);
    setImportarResult(null);
    setImportarDialog(true);
  };

  const handleImagemSelecionada = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      const [header, b64] = dataUrl.split(',');
      const mediaType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
      setImportarImage({ file, b64, mediaType, preview: dataUrl });
      setLoadingExtract(true);
      try {
        const resp = await api.post('/pedidos/extrair-confirmacoes', { imagem_b64: b64, media_type: mediaType }, { timeout: TIMEOUT_EXTRACAO_IA });
        const lista = resp.data.confirmacoes;
        if (!Array.isArray(lista) || lista.length === 0) {
          toast.error('Nenhum dado encontrado na imagem. Tente com outra imagem.');
          setLoadingExtract(false);
          return;
        }
        setImportarExtract(lista);
        setImportarStep('preview');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao extrair dados da imagem');
      } finally {
        setLoadingExtract(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmarImportacao = async () => {
    setLoadingImport(true);
    try {
      const resp = await api.put('/pedidos/importar-confirmacoes', { confirmacoes: importarExtract });
      setImportarResult(resp.data);
      setImportarStep('result');
      fetchPedidos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar confirmações');
    } finally {
      setLoadingImport(false);
    }
  };

  const handleFecharImportar = () => {
    setImportarDialog(false);
    setImportarStep('upload');
    setImportarImage(null);
    setImportarExtract([]);
    setImportarResult(null);
  };

  useEffect(() => { setCurrentPage(1); }, [filterStatus, filterSegmento, searchTerm, filterCliente, filterRepresentada, dateStart, dateEnd, dateFilterMode]);

  // Resolve nome de exibição: nome_fantasia do cadastro (quando preenchido) ou fallback via nomeFantasia()
  const clienteIdMap = useMemo(() => Object.fromEntries(clientesBase.map(c => [c.id, c])), [clientesBase]);
  const nomeExibicao = (p) => clienteIdMap[p.cliente_id]?.nome_fantasia || nomeFantasia(p.cliente_nome);

  const representadasUnicas = useMemo(() =>
    [...new Set(pedidos.map(p => p.representada_nome).filter(Boolean))].sort()
  , [pedidos]);

  const pedidosFiltradosSemCliente = useMemo(() =>
    pedidos.filter(p => {
      const matchRepresentada = !filterRepresentada || p.representada_nome === filterRepresentada;
      let matchDate = true;
      if (dateStart || dateEnd) {
        const de = getDataFiltro(p, dateFilterMode);
        if (dateStart && dateEnd) matchDate = de >= dateStart && de <= dateEnd;
        else if (dateStart) matchDate = de >= dateStart;
        else if (dateEnd) matchDate = de <= dateEnd;
      }
      return matchRepresentada && matchDate;
    })
  , [pedidos, filterRepresentada, dateStart, dateEnd, dateFilterMode]);

  const clientesUnicos = useMemo(() => {
    const seen = new Map();
    pedidosFiltradosSemCliente.forEach(p => {
      const clienteCadastro = clienteIdMap[p.cliente_id];
      const nomeDisplay = clienteCadastro?.nome_fantasia
        || nomeFantasia(p.cliente_nome);
      const key = clienteCadastro?.id || p.cliente_nome?.toUpperCase().trim();
      if (key && !seen.has(key)) {
        seen.set(key, {
          key,
          label: nomeDisplay
        });
      }
    });
    return [...seen.values()]
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [pedidosFiltradosSemCliente, clienteIdMap]);

  const segmentoContadores = useMemo(() => {
    const dateFiltrados = pedidos.filter(p => {
      if (!dateStart && !dateEnd) return true;
      const de = getDataFiltro(p, dateFilterMode);
      if (dateStart && dateEnd) return de >= dateStart && de <= dateEnd;
      if (dateStart) return de >= dateStart;
      if (dateEnd) return de <= dateEnd;
      return true;
    });
    return {
      TODOS: dateFiltrados.length,
      CAIXA: dateFiltrados.filter(p => classificarSegmento(p.numero_fe) === 'CAIXA').length,
      CHAPA: dateFiltrados.filter(p => classificarSegmento(p.numero_fe) === 'CHAPA').length,
    };
  }, [pedidos, dateStart, dateEnd, dateFilterMode]);

  const contadores = useMemo(() => {
    const pedidosFiltradosPorData = pedidos.filter(p => {
      if (!dateStart && !dateEnd) return true;
      const de = getDataFiltro(p, dateFilterMode);
      if (dateStart && dateEnd) return de >= dateStart && de <= dateEnd;
      if (dateStart) return de >= dateStart;
      if (dateEnd) return de <= dateEnd;
      return true;
    }).filter(p => filterSegmento === 'TODOS' || classificarSegmento(p.numero_fe) === filterSegmento);
    const c = { TODOS: pedidosFiltradosPorData.length, NAO_FATURADOS: 0, PENDENTE: 0, IMPLANTADO: 0, NF_EMITIDA: 0, CANCELADO: 0, ATRASADO: 0, SALDO: 0 };
    pedidosFiltradosPorData.forEach(p => {
      const sr = p.status;
      if (c[sr] !== undefined) c[sr]++;
      if (p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO') c.NAO_FATURADOS++;
      if (p.pedido_mae && p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO') c.SALDO++;
    });
    return c;
  }, [pedidos, dateStart, dateEnd, dateFilterMode, filterSegmento]);

  const totais = useMemo(() => {
    const normalize = (s) => String(s || '').replace(/[\s-]/g, '').toLowerCase();
    const lista = pedidos.filter(p => {
      const matchStatus = filterStatus === 'TODOS'
        || (filterStatus === 'SALDO' ? (!!p.pedido_mae && p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO')
          : filterStatus === 'NAO_FATURADOS' ? (p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO')
          : p.status === filterStatus);
      const matchSegmento = filterSegmento === 'TODOS' || classificarSegmento(p.numero_fe) === filterSegmento;
      const norm = normalize(searchTerm);
      const matchSearch = !norm || normalize(p.cliente_nome).includes(norm) || normalize(p.numero_fe).includes(norm) || normalize(p.numero_oc).includes(norm) || normalize(p.item_nome).includes(norm) || normalize(p.numero_fabrica).includes(norm);
      const matchCliente = !filterCliente || p.cliente_id === filterCliente || p.cliente_nome?.toUpperCase().trim() === filterCliente;
      const matchRepresentada = !filterRepresentada || p.representada_nome === filterRepresentada;
      let matchDate = true;
      if (dateStart || dateEnd) {
        const de = getDataFiltro(p, dateFilterMode);
        if (dateStart && dateEnd) matchDate = de >= dateStart && de <= dateEnd;
        else if (dateStart) matchDate = de >= dateStart;
        else if (dateEnd) matchDate = de <= dateEnd;
      }
      return matchStatus && matchSegmento && matchSearch && matchCliente && matchRepresentada && matchDate;
    });
    const listaSomavel = lista.filter(p => p.status !== 'CANCELADO');
    const peso = listaSomavel.reduce((a,p) => a + toNum(p.peso_total), 0);
    const valor = listaSomavel.reduce((a,p) => a + toNum(p.valor_total), 0);
    return {
      lista,
      peso, valor,
      fatorMedio: peso > 0 ? valor/peso : 0,
      comissao: listaSomavel.reduce((a,p) => a + toNum(p.comissao_valor || p.itens?.[0]?.comissao_valor || 0), 0),
      qtde: listaSomavel.reduce((a,p) => a + (parseInt(p.quantidade)||0), 0),
    };
  }, [pedidos, filterStatus, filterSegmento, searchTerm, filterCliente, filterRepresentada, dateStart, dateEnd, dateFilterMode]);

  const handleAbrirPdfDialog = () => {
    setPdfForm({
      data_inicio: dateStart || '',
      data_fim: dateEnd || '',
      status: filterStatus,
      ids: totais.lista.map(p => p.id).filter(Boolean),
    });
    setPdfDialog(true);
  };

  const handleExportPedidosPdf = async () => {
    setLoadingPdf(true);
    try {
      const payload = { ids: pdfForm.ids, status: pdfForm.status };
      const r = await api.post('/export/pedidos-pdf', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pedidos_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('PDF gerado!');
      setPdfDialog(false);
    } catch { toast.error('Erro ao gerar PDF'); }
    finally { setLoadingPdf(false); }
  };

  const handleDeleteClick = (e, pedido) => {
    e.stopPropagation();
    setDeletePedido(pedido);
    setDeleteDialog(true);
  };

  const handleConfirmarDelete = async () => {
    if (!deletePedido) return;
    try {
      await api.delete(`/pedidos/${deletePedido.id}`);
      toast.success('Pedido excluído!');
      setDeleteDialog(false);
      setDeletePedido(null);
      fetchPedidos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir pedido');
    }
  };


  const handleCancelarPedido = async () => {
    if (!deletePedido) return;
    try {
      await api.patch(`/pedidos/${deletePedido.id}/cancelar`);
      toast.success('Pedido cancelado!');
      setDeleteDialog(false); setDeletePedido(null);
      fetchPedidos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar pedido');
    }
  };

  const handleAbrirEntrega = (e, pedido) => {
    e.stopPropagation(); setEntregaPedido(pedido);
    setEntregaForm({...ENTREGA_FORM_INITIAL, qtde_entregue:String(pedido.quantidade||'')});
    setEntregaDialog(true);
  };

  const handleConfirmarEntrega = async () => {
    if (submittingEntrega) return;
    const valida = validarQuantidadePositiva(entregaForm.qtde_entregue, 'Quantidade entregue deve ser maior que zero');
    if (!valida.ok) {
      toast.error(valida.erro);
      return;
    }
    const qtdeEntregue = valida.qtde;
    const qtdePedida = parseInt(entregaPedido.quantidade)||0;
    const saldo = qtdePedida - qtdeEntregue;
    setSubmittingEntrega(true);
    try {
      const historicoEntrega = {
        qtde_pedida:qtdePedida, qtde_entregue:qtdeEntregue,
        variacao_percent:qtdePedida>0?((qtdeEntregue-qtdePedida)/qtdePedida*100).toFixed(1):0,
        motivo:entregaForm.motivo, observacao:entregaForm.observacao,
        registrado_em:new Date().toISOString(),
      };
      const materialPedido = materiais.find(m => m.numero_fe?.toUpperCase() === entregaPedido.numero_fe?.toUpperCase());
      const pesoUnit = materialPedido ? toNum(materialPedido.peso_unit) : qtdePedida>0?toNum(entregaPedido.peso_total)/qtdePedida:0;
      const precoMilheiro = toNum(entregaPedido.itens?.[0]?.valor_unitario||0);
      const comissaoPercent = toNum(entregaPedido.itens?.[0]?.comissao_percent||0);
      const novoValorTotal = (qtdeEntregue/1000)*precoMilheiro;
      const novoPesoTotal = qtdeEntregue*pesoUnit;
      const novaComissao = novoValorTotal*comissaoPercent/100;
      await api.put(`/pedidos/${entregaPedido.id}`, {
        ...entregaPedido, quantidade:qtdeEntregue, peso_total:novoPesoTotal,
        valor_total:novoValorTotal, comissao_valor:novaComissao,
        historico_entrega:historicoEntrega,
        itens:[{...(entregaPedido.itens?.[0]||{}), quantidade:qtdeEntregue, peso_calculado:novoPesoTotal, subtotal:novoValorTotal, comissao_valor:novaComissao}],
      });
      if (entregaForm.motivo === 'entrega_parcial' && saldo > 0) {
        const numMae = entregaPedido.numero_fabrica||'';
        const saldosExistentes = pedidos.filter(p => p.numero_fabrica?.startsWith(numMae+'-S')).length;
        const numSaldo = `${numMae}-S${saldosExistentes+1}`;
        const pesoSaldo=saldo*pesoUnit; const valorSaldo=(saldo/1000)*precoMilheiro;
        const comissaoSaldo=valorSaldo*comissaoPercent/100;
        await api.post('/pedidos', {
          cliente_id:entregaPedido.cliente_id, cliente_nome:entregaPedido.cliente_nome, item_nome:entregaPedido.item_nome,
          numero_fabrica:numSaldo, numero_fe:entregaPedido.numero_fe,
          numero_oc:entregaPedido.numero_oc, data_entrega:entregaPedido.data_entrega,
          quantidade:saldo, peso_total:pesoSaldo, valor_total:valorSaldo,
          comissao_valor:comissaoSaldo, status:'IMPLANTADO',
          pedido_mae:entregaPedido.numero_fabrica,
          representada_id:entregaPedido.representada_id, representada_nome:entregaPedido.representada_nome,
          observacoes:`Saldo do pedido ${entregaPedido.numero_fabrica}`,
          itens:[{...(entregaPedido.itens?.[0]||{}), quantidade:saldo, peso_calculado:pesoSaldo, subtotal:valorSaldo, comissao_valor:comissaoSaldo}],
        });
        toast.success(`Pedido saldo ${numSaldo} criado com ${saldo.toLocaleString('pt-BR')} unidades!`);
      } else { toast.success('Entrega registrada!'); }
      setEntregaDialog(false); setEntregaPedido(null); fetchPedidos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao registrar entrega'); }
    finally { setSubmittingEntrega(false); }
  };

  const totalPages = Math.ceil(totais.lista.length / itemsPerPage);
  const pedidosPagina = totais.lista.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (pedidoPrintAtual) {
    const p = pedidoPrintAtual;
    const cliente = clienteIdMap[p.cliente_id] || {};
    const material = materiais.find(m => (m.numero_fe || m.codigo) === p.numero_fe);
    const ocCliente = p.numero_oc?.startsWith('OC-') ? '' : (p.numero_oc || '');

    const dataCriacaoAA = p.criado_em
      ? (() => {
          const d = new Date(p.criado_em);
          return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${String(d.getUTCFullYear()).slice(-2)}`;
        })()
      : '—';

    const ipiIsento = !!cliente.isento_ipi;
    const ipiPercent = ipiIsento ? 0 : (material?.ipi ?? 15);
    const ipiValor = toNum(p.valor_total) * ipiPercent / 100;
    const totalComIpi = toNum(p.valor_total) + ipiValor;

    const TOTAL_LINHAS = 15;
    const itensReais = p.itens?.length
      ? p.itens
      : [{ quantidade: p.quantidade, peso_calculado: p.peso_total, valor_unitario: 0, subtotal: p.valor_total }];
    const linhasTabela = [
      ...itensReais,
      ...Array.from({ length: Math.max(0, TOTAL_LINHAS - itensReais.length) }).map(() => null),
    ];

    const totalQtde = itensReais.reduce((a, it) => a + (parseInt(it.quantidade) || 0), 0);

    const getEspc = (fe) => {
      if (!fe) return '';
      const u = fe.toUpperCase();
      if (u.includes('-CX')) return 'CX NORMAL';
      if (u.includes('-CH')) return 'CH';
      return '';
    };

    const bord = '1px solid #999';
    const cellStyle = { border: bord, padding: '2px 4px', fontSize: 8 };
    const lblStyle = { fontWeight: 700 };

    return (
      <div style={{ fontFamily: 'Arial, sans-serif', background: 'white' }}>
        <style>{`
          @page { size: A4 landscape; margin: 6mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          * { box-sizing: border-box; }
        `}</style>

        {/* Botões — não imprimem */}
        <div className="no-print" style={{ display: 'flex', gap: 8, padding: '8px 16px', background: '#f0f0f0', borderBottom: '1px solid #ccc' }}>
          <button onClick={() => setPedidoPrintAtual(null)} style={{ padding: '8px 20px', background: '#444', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>← Voltar</button>
          <button onClick={() => window.print()} style={{ padding: '8px 20px', background: '#166534', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>🖨 Imprimir / PDF</button>
        </div>

        <div style={{ padding: 8, fontSize: 8 }}>

          {/* 1. CABEÇALHO */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: bord, marginBottom: 3 }}>
            <tbody>
              <tr>
                <td style={{ width: '15%', border: bord, padding: 4, textAlign: 'center', verticalAlign: 'middle' }}>
                  <img src={`data:image/jpeg;base64,${LOGO_B64}`} alt="Conpel" style={{ height: 45, width: 'auto' }} />
                </td>
                <td style={{ width: '55%', border: bord, padding: 4, textAlign: 'center', verticalAlign: 'middle', fontSize: 8 }}>
                  <div>Rod.BR 101 KM 06 - VALE DO GRAMAME - CEP 58322-000</div>
                  <div>Fone: (83) 3690-0560</div>
                  <div>CNPJ 55.330.530/0001-90 - IE 16.497.183-1</div>
                  <div>Conde - PB</div>
                </td>
                <td style={{ width: '30%', border: bord, padding: 4, textAlign: 'right', verticalAlign: 'middle', fontSize: 8 }}>
                  <div>ORDEM DE COMPRA: {ocCliente}</div>
                  <div>{dataCriacaoAA}</div>
                  <div>RUBENS BAMELO</div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* 2. DADOS DO CLIENTE */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: bord, marginBottom: 3, fontSize: 8 }}>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, width: '35%' }}><span style={lblStyle}>Razão Social:</span> {cliente.nome || p.cliente_nome || ''}</td>
                <td style={{ ...cellStyle, width: '20%' }}><span style={lblStyle}>Fantasia:</span> {cliente.nome_fantasia || ''}</td>
                <td style={{ ...cellStyle, width: '20%' }}><span style={lblStyle}>Contato:</span> {cliente.comprador || ''}</td>
                <td style={{ ...cellStyle, width: '25%' }}><span style={lblStyle}>Setor:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>End. Faturamento:</span> {cliente.endereco || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>Cidade:</span> {cliente.cidade || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>UF:</span> {cliente.estado || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>CEP:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>Fone:</span> {cliente.telefone || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>E-mail:</span> {cliente.email || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>CNPJ:</span> {cliente.cnpj || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>Insc. Estadual:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>End. Cobrança:</span> {cliente.endereco || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>Cidade:</span></td>
                <td style={cellStyle}><span style={lblStyle}>UF:</span></td>
                <td style={cellStyle}><span style={lblStyle}>CEP:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>Contato:</span></td>
                <td style={cellStyle}><span style={lblStyle}>Depto.:</span></td>
                <td style={cellStyle}><span style={lblStyle}>Fone:</span></td>
                <td style={cellStyle}><span style={lblStyle}>E-mail:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>End. Entrega:</span> {cliente.endereco || ''}</td>
                <td style={cellStyle}><span style={lblStyle}>Cidade:</span></td>
                <td style={cellStyle}><span style={lblStyle}>UF:</span></td>
                <td style={cellStyle}><span style={lblStyle}>CEP:</span></td>
              </tr>
              <tr>
                <td style={cellStyle}><span style={lblStyle}>Contato:</span></td>
                <td style={cellStyle}><span style={lblStyle}>Depto.:</span></td>
                <td style={cellStyle}><span style={lblStyle}>Fone:</span></td>
                <td style={cellStyle}><span style={lblStyle}>E-mail:</span></td>
              </tr>
            </tbody>
          </table>

          {/* 3. TABELA DE ITENS — 15 linhas fixas */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: bord, marginBottom: 3, fontSize: 8 }}>
            <thead>
              <tr style={{ background: '#E0E0E0' }}>
                <th style={{ ...cellStyle, textAlign: 'center', width: '3%' }}>Nº</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '6%' }}>Cod. Cliente</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '9%' }}>FE CONPEL</th>
                <th style={{ ...cellStyle, textAlign: 'left', width: '20%' }}>Descrição</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '8%' }}>Data Entrega</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '8%' }}>Quantidade (UND)</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '7%' }}>Peso (UND)</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '8%' }}>Peso Total</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '10%' }}>Espc. Produto CH/CX/GM²/R/K/H</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '9%' }}>Preço milheiro</th>
                <th style={{ ...cellStyle, textAlign: 'center', width: '6%' }}>Fator</th>
                <th style={{ ...cellStyle, textAlign: 'right', width: '9%' }}>SubTotal</th>
              </tr>
            </thead>
            <tbody>
              {linhasTabela.map((it, i) => {
                if (!it) {
                  return (
                    <tr key={i} style={{ height: 18 }}>
                      <td style={{ ...cellStyle, textAlign: 'center', color: '#bbb' }}>{i + 1}</td>
                      {Array.from({ length: 11 }).map((_, j) => <td key={j} style={{ border: bord }}></td>)}
                    </tr>
                  );
                }
                const qtde = parseInt(it.quantidade || p.quantidade) || 0;
                const pesoTotalIt = toNum(it.peso_calculado ?? p.peso_total);
                const pesoUnitIt = qtde > 0 ? pesoTotalIt / qtde : 0;
                const precoMilh = toNum(it.valor_unitario || 0);
                const subtotalIt = toNum(it.subtotal ?? p.valor_total);
                const feCode = p.numero_fe || '';
                const dataEntregaFmt = p.data_entrega
                  ? new Date(p.data_entrega + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                  : '';
                return (
                  <tr key={i} style={{ height: 18 }}>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>{i + 1}</td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}></td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>{feCode}</td>
                    <td style={{ ...cellStyle, textAlign: 'left' }}>{p.item_nome || ''}</td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>{dataEntregaFmt}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{qtde.toLocaleString('pt-BR')}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{fmt(pesoUnitIt, 3)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{fmt(pesoTotalIt, 3)}Kg</td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>{getEspc(feCode)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{precoMilh > 0 ? brl(precoMilh) : ''}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{precoMilh > 0 ? fmt(precoMilh / 1000, 2) : ''}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{subtotalIt > 0 ? brl(subtotalIt) : ''}</td>
                  </tr>
                );
              })}
              {/* Linha de totais */}
              <tr style={{ background: '#E0E0E0', fontWeight: 700 }}>
                <td colSpan={5} style={{ border: bord }}></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>{totalQtde.toLocaleString('pt-BR')}</td>
                <td style={{ border: bord }}></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>{fmt(toNum(p.peso_total), 2)}Kg</td>
                <td colSpan={4} style={{ border: bord }}></td>
              </tr>
            </tbody>
          </table>

          {/* 4. RODAPÉ COMERCIAL */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: bord, marginBottom: 3, fontSize: 8 }}>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, width: '40%' }}>
                  <span style={lblStyle}>Condição de Pagamento:</span> {p.condicao_pagamento || ''}
                </td>
                <td style={{ ...cellStyle, width: '30%' }}>
                  Produto Paletizado: SIM □ &nbsp; NÃO □
                </td>
                <td style={{ ...cellStyle, width: '30%', textAlign: 'right' }}>
                  <span style={lblStyle}>Total Geral R$</span> {brl(p.valor_total)}
                </td>
              </tr>
              <tr>
                <td style={cellStyle}>
                  Frete: CIF □ &nbsp; FOB □ &nbsp;&nbsp; <span style={lblStyle}>Transportadora:</span>
                </td>
                <td style={cellStyle}><span style={lblStyle}>Fone:</span></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {ipiIsento
                    ? <span style={lblStyle}>IPI: ISENTO</span>
                    : <span><span style={lblStyle}>IPI:</span> {ipiPercent}% R$ {brl(ipiValor)}</span>}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ border: bord }}></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  <span style={lblStyle}>Total com IPI(R$)</span> {brl(totalComIpi)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* 5. OBSERVAÇÕES */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: bord, fontSize: 8 }}>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, minHeight: 22 }}>
                  <span style={lblStyle}>Observações:</span> {p.observacoes || ''}
                </td>
              </tr>
              <tr>
                <td style={{ border: bord, height: 20 }}></td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Sincronizando Carteira...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Gestão de Pedidos</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Carteira · {totais.lista.length} registros encontrados</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input placeholder="Buscar pedido, cliente, FE..." className="text-xs font-semibold bg-transparent outline-none w-44"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
          </div>
          <select value={filterCliente} onChange={e => setFilterCliente(e.target.value)}
            className="border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all cursor-pointer shadow-sm max-w-[180px]">
            <option value="">Todos os clientes</option>
            {clientesUnicos.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {representadasUnicas.length > 0 && (
            <select value={filterRepresentada} onChange={e => setFilterRepresentada(e.target.value)}
              className="border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all cursor-pointer shadow-sm">
              <option value="">Todas representadas</option>
              {representadasUnicas.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <DateRangePicker
            dateStart={dateStart} dateEnd={dateEnd}
            mode={dateFilterMode} onModeChange={setDateFilterMode}
            onChange={({start,end}) => {setDateStart(start||'');setDateEnd(end||'');}}
            onClear={() => {setDateStart('');setDateEnd('');}}
          />
          <button onClick={handleAbrirPdfDialog}
            className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
            <FileText size={14}/> Exportar PDF
          </button>
          <button onClick={handleAbrirImportar}
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
            <Upload size={14}/> Importar SAC
          </button>
          <button onClick={() => navigate('/pedidos/novo')}
            className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
            <Plus size={14}/> Novo Pedido
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          {label:'Peso Total', value:`${fmt(totais.peso/1000,3)} TON`, sub:`${fmt(totais.peso)} KG`, icon:Weight, cor:'#1E40AF', fundo:'#EFF6FF'},
          {label:'Qtde Total', value:totais.qtde.toLocaleString('pt-BR'), icon:Package, cor:'#374151', fundo:'#F9FAFB'},
          {label:'Valor Total', value:`R$ ${fmt(totais.valor)}`, icon:DollarSign, cor:'#166534', fundo:'#DCFCE7'},
          {label:'Fator Médio R$/KG', value:`R$ ${fmt(totais.fatorMedio)}`, icon:TrendingUp, cor:'#1D4ED8', fundo:'#DBEAFE'},
          {label:'Comissão Total', value:`R$ ${fmt(totais.comissao)}`, icon:TrendingUp, cor:'#92400E', fundo:'#FEF3C7'},
        ].map((k,i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{background:k.fundo}}>
                <k.icon size={13} style={{color:k.cor}}/>
              </div>
            </div>
            <p className="text-base font-black" style={{color:k.cor}}>{k.value}</p>
            {k.sub && <p className="text-xs text-slate-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* ── FILTROS SEGMENTO ── */}
      <div className="flex flex-wrap gap-2 mb-3">
        {[
          { key: 'TODOS', label: 'Todos' },
          { key: 'CAIXA', label: 'CAIXA' },
          { key: 'CHAPA', label: 'CHAPA' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilterSegmento(key)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${filterSegmento===key ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-600'}`}>
            {label} <span className="opacity-60">({segmentoContadores[key]||0})</span>
          </button>
        ))}
      </div>

      {/* ── FILTROS STATUS ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['TODOS','NAO_FATURADOS',...STATUS_OPTIONS,'ATRASADO','SALDO'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${filterStatus===s ? 'bg-[#0A3D73] text-white border-[#0A3D73] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0A3D73]'}`}>
            {s==='TODOS'?'Todos':s==='SALDO'?'Saldo':s==='NAO_FATURADOS'?'Não Faturados':STATUS_CONFIG[s]?.label} <span className="opacity-60">({contadores[s]||0})</span>
          </button>
        ))}
      </div>

      {/* ── TABELA ── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                {['Pedido','FE','OC','Referência','Cliente','Representada','Entrega','Peso KG','Qtde','Fator','Total','Status','Ações'].map((h,i) => (
                  <th key={i} className={`px-3 py-3 whitespace-nowrap ${['Entrega','Peso KG','Qtde','Fator','Total'].includes(h)?'text-right':h==='Status'||h==='Ações'?'text-center':'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {totais.lista.length === 0 ? (
                <tr><td colSpan={13}>
                  <EmptyState
                    icon={
                      <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                        <path d="M10 20h52a2 2 0 012 2v6H8v-6a2 2 0 012-2z" stroke="#4A90D9" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M14 28h44v30a2 2 0 01-2 2H16a2 2 0 01-2-2V28z" stroke="#4A90D9" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M29 28v-8h14v8" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="36" y1="38" x2="36" y2="50" stroke="#7BB8F0" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2"/>
                      </svg>
                    }
                    title="Nenhum pedido encontrado"
                    subtitle="Tente ajustar os filtros ou crie um novo pedido"
                    actionLabel="+ Novo Pedido"
                    onAction={() => navigate('/pedidos/novo')}
                  />
                </td></tr>
              ) : pedidosPagina.map((p,idx) => {
                const travado = p.status==='NF_EMITIDA' && !isAdmin;
                const fator = toNum(p.peso_total)>0 ? toNum(p.valor_total)/toNum(p.peso_total) : 0;
                const statusReal = p.status;
                const statusCfg = STATUS_CONFIG[statusReal]||STATUS_CONFIG.PENDENTE;
                return (
                  <tr key={p.id} onClick={() => navigate(`/pedidos/${p.id}`)}
                    className={`text-xs border-b border-slate-100 transition-colors
                      ${idx%2===0?'bg-white':'bg-slate-50/60'}
                      ${statusReal==='ATRASADO'?'border-l-2 border-l-red-400':''}
                      hover:bg-blue-50 cursor-pointer`}>
                    <td className="px-3 py-2.5 font-black text-[#0A3D73] font-mono whitespace-nowrap">
                      {p.numero_fabrica||<span className="text-slate-300 italic font-normal text-xs">— aguardando —</span>}
                    </td>
                    <td className="px-3 py-2.5 font-bold text-blue-700 font-mono whitespace-nowrap">
                      {p.itens?.length > 1
                        ? <span className="flex items-center gap-1">
                            <span>{p.itens[0]?.numero_fe || p.numero_fe || '---'}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1 rounded font-black">+{p.itens.length - 1}</span>
                          </span>
                        : (p.numero_fe || '---')
                      }
                    </td>
                    <td className="px-3 py-2.5 font-bold text-slate-600 whitespace-nowrap">{p.numero_oc||'---'}</td>
                    <td className="px-3 py-2.5 font-semibold max-w-[140px]">
                      {p.itens?.length > 1
                        ? <span className="flex items-center gap-1">
                            <span className="truncate">{p.itens[0]?.item_nome || p.item_nome || '---'}</span>
                            <span className="shrink-0 text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1 rounded font-black">+{p.itens.length - 1}</span>
                          </span>
                        : <span className="block truncate">{p.item_nome || '---'}</span>
                      }
                    </td>
                    <td className="px-3 py-2.5 font-black whitespace-nowrap">
                      <span title={p.cliente_nome}>{nomeExibicao(p)}</span>
                      {p.pedido_mae && p.status !== 'NF_EMITIDA' && <span className="ml-1 text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1 rounded font-black">SALDO</span>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {p.representada_nome
                        ? <span className="block max-w-[90px] truncate text-xs font-bold text-[#0A3D73]" title={p.representada_nome}>{p.representada_nome}</span>
                        : <span className="text-slate-300 text-xs">—</span>
                      }
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-500 whitespace-nowrap">{fmtDate(p.data_entrega)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{fmt(p.peso_total)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{(parseInt(p.quantidade)||0).toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2.5 text-right font-bold text-blue-600 whitespace-nowrap">R$ {fmt(fator)}</td>
                    <td className="px-3 py-2.5 text-right font-black text-slate-800 whitespace-nowrap">R$ {fmt(p.valor_total)}</td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`px-2 py-0.5 text-xs font-black border rounded-full ${statusCfg.cls}`}>{statusCfg.label}</span>
                        {p.status==='IMPLANTADO' && isAdmin && (
                          <button onClick={(e) => handleAbrirEntrega(e,p)}
                            className="text-xs font-black px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full hover:bg-emerald-600 hover:text-white transition-all flex items-center gap-1">
                            <GitBranch size={9}/> Entregar
                          </button>
                        )}
                        {p.historico_entrega && (
                          <span className={`text-xs font-black ${Math.abs(parseFloat(p.historico_entrega.variacao_percent))<=10?'text-emerald-600':Math.abs(parseFloat(p.historico_entrega.variacao_percent))<=20?'text-orange-500':'text-red-600'}`}>
                            {parseFloat(p.historico_entrega.variacao_percent)>=0?'+':''}{p.historico_entrega.variacao_percent}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!travado) navigate(`/pedidos/editar/${p.id}`); }}
                          disabled={travado}
                          className={`p-1.5 rounded-lg transition-all ${travado ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-[#0A3D73] hover:bg-blue-50'}`}
                          title={travado ? 'NF emitida — apenas admin pode editar' : 'Editar pedido'}>
                          {travado ? <Lock size={13}/> : <Edit2 size={13}/>}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setPedidoPrintAtual(p); }}
                          className="p-1.5 rounded-lg text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                          title="Pedido PDF">
                          <Printer size={13}/>
                        </button>
                        {isAdmin && p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO' && (
                          <Link to={`/notas-fiscais/nova?pedido_id=${p.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all inline-flex"
                            title="Emitir NF">
                            <ReceiptText size={13}/>
                          </Link>
                        )}
                        {p.status !== 'CANCELADO' && !travado && (
                          <button onClick={(e) => handleDeleteClick(e, p)}
                            className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
                            title="Cancelar / Excluir pedido">
                            <Trash2 size={13}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {totais.lista.length > 0 && (
              <tfoot>
                <tr className="bg-[#0A3D73] text-white text-xs font-black">
                  <td colSpan={14} className="px-3 py-2.5 text-right uppercase tracking-wide">↓ Ver totais abaixo</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── TOTAIS FORA DO SCROLL ── */}
        {totais.lista.length > 0 && (
          <div className="border-t-2 border-[#0A3D73] bg-[#0A3D73] text-white">
            <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 text-xs font-black">
              <span className="uppercase tracking-wide opacity-70">Totais — {totais.lista.length} pedidos</span>
              <div className="flex flex-wrap gap-6">
                <div className="text-center">
                  <p className="opacity-60 font-bold text-xs">Peso Total</p>
                  <p className="font-black font-mono text-sm">{fmt(totais.peso)} KG</p>
                </div>
                <div className="text-center">
                  <p className="opacity-60 font-bold text-xs">Qtde Total</p>
                  <p className="font-black font-mono text-sm">{totais.qtde.toLocaleString('pt-BR')}</p>
                </div>
                <div className="text-center">
                  <p className="opacity-60 font-bold text-xs">Fator Médio</p>
                  <p className="font-black font-mono text-sm">R$ {fmt(totais.fatorMedio)}</p>
                </div>
                <div className="text-center">
                  <p className="opacity-60 font-bold text-xs">Valor Total</p>
                  <p className="font-black font-mono text-sm text-emerald-300">R$ {fmt(totais.valor)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PAGINAÇÃO ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer">
                {[20, 40, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14}/>
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {totais.lista.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── DIALOG EXPORTAR PDF ── */}
      <Dialog open={pdfDialog} onOpenChange={(o) => { setPdfDialog(o); }}>
        <DialogContent className="max-w-sm bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-5 bg-[#0A3D73] text-white">
            <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
              <FileText size={15}/> Exportar Pedidos — PDF
            </DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block">Data Início</label>
                <Input type="date" value={pdfForm.data_inicio}
                  onChange={e => setPdfForm(f => ({...f, data_inicio: e.target.value}))}
                  className="w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block">Data Fim</label>
                <Input type="date" value={pdfForm.data_fim}
                  onChange={e => setPdfForm(f => ({...f, data_fim: e.target.value}))}
                  className="w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block">Status</label>
              <select value={pdfForm.status} onChange={e => setPdfForm(f => ({...f, status: e.target.value}))}
                className="w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all cursor-pointer">
                <option value="TODOS">Todos</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.label}</option>)}
                <option value="ATRASADO">{STATUS_CONFIG.ATRASADO.label}</option>
                <option value="SALDO">Saldo</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <Button type="button" onClick={() => setPdfDialog(false)}
                className="rounded-xl text-xs font-black uppercase h-10 flex-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                Cancelar
              </Button>
              <Button type="button" onClick={handleExportPedidosPdf} disabled={loadingPdf}
                className="bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white rounded-xl text-xs font-black uppercase h-10 flex-1">
                <FileText size={12} className="mr-1"/> {loadingPdf ? 'Gerando...' : 'Gerar PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG CANCELAR / EXCLUIR ── */}
      {deleteDialog && deletePedido && (
        <Dialog open={deleteDialog} onOpenChange={(o) => { setDeleteDialog(o); if (!o) setDeletePedido(null); }}>
          <DialogContent className="max-w-sm bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
            <DialogHeader className="p-5 bg-[#0A3D73] text-white">
              <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
                <Trash2 size={15}/> Ação no Pedido
              </DialogTitle>
            </DialogHeader>
            <div className="p-5 space-y-4">
              <p className="text-sm font-semibold text-slate-700">
                O que deseja fazer com o pedido <span className="font-black text-[#0A3D73]">{deletePedido.numero_fabrica || '—'}</span> de <span className="font-black">{nomeExibicao(deletePedido)}</span>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 font-semibold">
                <strong className="font-black">Cancelar</strong> mantém o registro no histórico com status Cancelado. <strong className="font-black">Excluir</strong> remove definitivamente (apenas admin).
              </div>
              <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                <Button type="button" onClick={handleCancelarPedido}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-black uppercase h-10 w-full">
                  <X size={12} className="mr-1"/> Cancelar Pedido
                </Button>
                {isAdmin && (
                  <Button type="button" onClick={handleConfirmarDelete}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase h-10 w-full">
                    <Trash2 size={12} className="mr-1"/> Excluir Definitivamente
                  </Button>
                )}
                <Button type="button" onClick={() => { setDeleteDialog(false); setDeletePedido(null); }}
                  className="rounded-xl text-xs font-black uppercase h-10 w-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                  Voltar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}


      {/* ── DIALOG IMPORTAR CONFIRMAÇÕES SAC ── */}
      {importarDialog && (
        <Dialog open={importarDialog} onOpenChange={(o) => { if (!o) handleFecharImportar(); }}>
          <DialogContent className="max-w-2xl bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
            <DialogHeader className="p-5 bg-[#0A3D73] text-white">
              <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
                <Upload size={15}/>
                {importarStep === 'upload' && 'Importar Confirmações da Fábrica (SAC)'}
                {importarStep === 'preview' && `Prévia — ${importarExtract.length} linha(s) extraída(s)`}
                {importarStep === 'result' && 'Resultado da Importação'}
              </DialogTitle>
            </DialogHeader>

            {/* ── STEP UPLOAD ── */}
            {importarStep === 'upload' && (
              <div className="p-6 space-y-5">
                <p className="text-xs font-semibold text-slate-500">
                  Faça upload do print do SAC da fábrica. A IA vai extrair os dados da tabela automaticamente.
                </p>
                {loadingExtract ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Analisando imagem com IA...</p>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-[#0A3D73] hover:bg-blue-50 transition-all">
                      <Upload size={32} className="mx-auto text-slate-300 mb-3"/>
                      <p className="text-sm font-black text-slate-600">Clique para selecionar ou arraste a imagem</p>
                      <p className="text-xs text-slate-400 font-semibold mt-1">PNG, JPG, JPEG — máx. 5 MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={(e) => handleImagemSelecionada(e.target.files?.[0])}
                    />
                  </label>
                )}
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <Button type="button" onClick={handleFecharImportar}
                    className="rounded-xl text-xs font-black uppercase h-9 px-5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* ── STEP PREVIEW ── */}
            {importarStep === 'preview' && (
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 font-semibold">
                  Revise os dados extraídos antes de confirmar. Pedidos são localizados pelo <span className="font-black">Nº Pedido</span> (campo "numero_fabrica").
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Nº Pedido</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">FE</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">OC</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Dt. Entrega</th>
                        <th className="px-3 py-2 text-right font-black text-slate-500 uppercase tracking-wide">Qtde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importarExtract.map((c, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 font-mono font-bold text-slate-800">{c.numero_pedido}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{c.numero_fe || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{c.oc || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{c.data_entrega || '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-800">
                            {c.qtde ? Number(c.qtde).toLocaleString('pt-BR') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3 pt-2 border-t border-slate-100">
                  <Button type="button" onClick={() => setImportarStep('upload')}
                    className="rounded-xl text-xs font-black uppercase h-10 flex-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                    Voltar
                  </Button>
                  <Button type="button" onClick={handleConfirmarImportacao} disabled={loadingImport}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase h-10 flex-1 disabled:opacity-60 disabled:cursor-not-allowed">
                    <CheckCircle2 size={13} className="mr-1.5"/>
                    {loadingImport ? 'Importando...' : `Confirmar Importação (${importarExtract.length})`}
                  </Button>
                </div>
              </div>
            )}

            {/* ── STEP RESULT ── */}
            {importarStep === 'result' && importarResult && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                    <p className="text-3xl font-black text-emerald-700">{importarResult.atualizados?.length ?? 0}</p>
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-600 mt-1">Pedidos atualizados</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <p className="text-3xl font-black text-red-700">{importarResult.nao_encontrados?.length ?? 0}</p>
                    <p className="text-xs font-bold uppercase tracking-wide text-red-600 mt-1">Não encontrados</p>
                  </div>
                </div>
                {importarResult.nao_encontrados?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-xs font-black text-amber-800 mb-2 uppercase tracking-wide">Pedidos não encontrados:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {importarResult.nao_encontrados.map((n, i) => (
                        <span key={i} className="bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5 text-xs font-mono font-bold">
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <Button type="button" onClick={handleFecharImportar}
                    className="bg-[#0A3D73] hover:bg-[#082D54] text-white rounded-xl text-xs font-black uppercase h-10 px-8">
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── DIALOG ENTREGA ── */}
      {entregaDialog && entregaPedido && (
        <Dialog open={entregaDialog} onOpenChange={(o)=>{setEntregaDialog(o);if(!o)setEntregaPedido(null);}}>
          <DialogContent className="max-w-md bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
            <DialogHeader className="p-5 bg-[#0A3D73] text-white">
              <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
                <GitBranch size={15}/> Registrar Entrega — {entregaPedido.numero_fabrica}
              </DialogTitle>
            </DialogHeader>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs">
                <p className="font-black text-blue-800">{nomeExibicao(entregaPedido)} — {entregaPedido.item_nome}</p>
                <p className="text-blue-600 font-bold mt-1">Qtde pedida: <span className="font-black">{(entregaPedido.quantidade||0).toLocaleString('pt-BR')} unidades</span></p>
              </div>
              <div>
                <label className={lbl}>Qtde Entregue *</label>
                <Input type="number" min="1" step="1" value={entregaForm.qtde_entregue} onChange={e=>setEntregaForm({...entregaForm,qtde_entregue:e.target.value})} className={inp}/>
                {entregaForm.qtde_entregue && entregaPedido.quantidade && (() => {
                  const v = ((parseInt(entregaForm.qtde_entregue)-entregaPedido.quantidade)/entregaPedido.quantidade*100);
                  const abs = Math.abs(v);
                  const cor = abs<=10?'text-emerald-700 bg-emerald-50 border-emerald-200':abs<=20?'text-orange-600 bg-orange-50 border-orange-200':'text-red-700 bg-red-50 border-red-200';
                  return (
                    <div className={`mt-1.5 px-3 py-1.5 border rounded-lg text-xs font-black ${cor}`}>
                      Variação: {v>=0?'+':''}{v.toFixed(1)}%
                      {parseInt(entregaForm.qtde_entregue)<entregaPedido.quantidade && <span className="ml-2">· Saldo: {(entregaPedido.quantidade-parseInt(entregaForm.qtde_entregue)).toLocaleString('pt-BR')} un</span>}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className={lbl}>Como tratar o saldo?</label>
                <div className="space-y-2">
                  {[
                    {value:'cliente_aceitou', label:'✅ Cliente aceitou — fechar', desc:'Pedido liquidado com a qtde entregue'},
                    {value:'fabrica_liquidou', label:'🏭 Fábrica liquidou — fechar', desc:'Fábrica não produzirá mais deste pedido'},
                    {value:'entrega_parcial', label:'📦 Entrega parcial — criar saldo', desc:`Saldo de ${Math.max(0,(entregaPedido.quantidade||0)-(parseInt(entregaForm.qtde_entregue)||0)).toLocaleString('pt-BR')} un vira novo pedido`},
                  ].map(op => (
                    <label key={op.value} className={`flex items-start gap-2 p-3 border rounded-xl cursor-pointer transition-all ${entregaForm.motivo===op.value?'bg-blue-50 border-blue-400':'bg-white border-slate-200 hover:border-slate-300'}`}>
                      <input type="radio" name="motivo" value={op.value} checked={entregaForm.motivo===op.value} onChange={e=>setEntregaForm({...entregaForm,motivo:e.target.value})} className="mt-0.5"/>
                      <div>
                        <p className="text-xs font-black">{op.label}</p>
                        <p className="text-xs text-slate-400 font-semibold mt-0.5">{op.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div><label className={lbl}>Observação</label>
                <input value={entregaForm.observacao} onChange={e=>setEntregaForm({...entregaForm,observacao:e.target.value})} className={`${inp} h-16`}/>
              </div>
              <div className="flex gap-3 pt-2 border-t border-slate-100">
                <Button type="button" onClick={()=>setEntregaDialog(false)} className="rounded-xl text-xs font-black uppercase h-10 flex-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">Cancelar</Button>
                <Button type="button" onClick={handleConfirmarEntrega} disabled={!validarQuantidadePositiva(entregaForm.qtde_entregue).ok || submittingEntrega}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase h-10 flex-1 disabled:opacity-60 disabled:cursor-not-allowed">
                  <CheckCircle2 size={12} className="mr-1"/> {submittingEntrega ? 'Salvando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Pedidos;